/**
 * icons — a small, hand-authored inline-SVG outline icon set that replaces
 * the emoji glyphs the sidebar used to render (📊📹🔔🎯✅📋📄🎥📐👷🚗🤖⚙️❓).
 * Emoji render differently per OS/font and read as a placeholder rather
 * than a deliberate icon system; a single `currentColor` stroke set
 * inherits the nav item's active/inactive text color automatically.
 *
 * Style: 24×24 viewBox, `stroke-current`, `stroke-width 1.75` (the 2026-09
 * redesign's Lucide-like line weight), round caps/joins,
 * no fill (a couple of icons fill one small dot to read as a device/status
 * light — see IconMonitor, IconHelp). Default rendered size is 20×20
 * (`w-5 h-5`); pass `className` to override.
 *
 * `NAV_ICON_MAP` keys match the `NavItem.key` values in
 * `components/layout/Shell.tsx` — look an icon up by nav key there instead
 * of importing each icon by name.
 */
import type { ComponentType, ReactNode, SVGProps } from "react";

export type IconProps = SVGProps<SVGSVGElement>;

function IconBase({ className = "w-5 h-5", children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Sidebar — dashboard. */
export function IconDashboard(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </IconBase>
  );
}

/** Sidebar — live monitor (camera feed on a screen). */
export function IconMonitor(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <circle cx="17" cy="8" r="1.1" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

/** Sidebar — alerts. */
export function IconAlerts(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M6 8a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </IconBase>
  );
}

/** Sidebar — AI inspector (scan/target). */
export function IconAiInspector(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </IconBase>
  );
}

/** Sidebar — compliance (shield + check). */
export function IconCompliance(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 3l7 3v6c0 4.8-3.2 8.4-7 9.5-3.8-1.1-7-4.7-7-9.5V6l7-3z" />
      <path d="M9 12.3l2 2 4-4.3" />
    </IconBase>
  );
}

/** Sidebar — safety logbook (pinkas). */
export function IconPinkas(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <rect x="9" y="2.3" width="6" height="3.4" rx="1" />
      <path d="M8.5 10.5h7M8.5 13.5h7M8.5 16.5h4.5" />
    </IconBase>
  );
}

/** Sidebar — reports (document with a small bar chart). */
export function IconReports(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 16v-3M12 16V9M16 16v-5" />
    </IconBase>
  );
}

/** Sidebar — cameras (device body + lens). */
export function IconCameras(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="3" y="7" width="12" height="10" rx="2" />
      <path d="M15 10.3 21 7.5v9l-6-2.8z" />
      <circle cx="9" cy="12" r="2" />
    </IconBase>
  );
}

/** Sidebar — zones (map pin). */
export function IconZones(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 21s-7-7.6-7-12.4A7 7 0 0 1 19 8.6C19 13.4 12 21 12 21z" />
      <circle cx="12" cy="8.6" r="2.4" />
    </IconBase>
  );
}

/** Sidebar — workers (person). */
export function IconWorkers(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 20c0-4 2.9-6.6 6.5-6.6s6.5 2.6 6.5 6.6" />
    </IconBase>
  );
}

/** Sidebar — vehicles (car). */
export function IconVehicles(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 16.5 5.3 11a2 2 0 0 1 1.9-1.4h9.6a2 2 0 0 1 1.9 1.4l1.3 5.5" />
      <path d="M6 11l1-3.2A1.6 1.6 0 0 1 8.5 6.7h7a1.6 1.6 0 0 1 1.5 1.1L18 11" />
      <path d="M4 16.5h16" />
      <circle cx="7.5" cy="17.3" r="1.6" />
      <circle cx="16.5" cy="17.3" r="1.6" />
    </IconBase>
  );
}

/** Sidebar — AI hub (chip). No commercial model names — a generic chip glyph. */
export function IconAiHub(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
      <path d="M9.5 7V4M14.5 7V4M9.5 20v-3M14.5 20v-3M7 9.5H4M7 14.5H4M20 9.5h-3M20 14.5h-3" />
    </IconBase>
  );
}

/** Sidebar — settings (sliders). */
export function IconSettings(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 6h16M4 12h16M4 18h16" />
      <circle cx="9" cy="6" r="2" />
      <circle cx="15" cy="12" r="2" />
      <circle cx="7" cy="18" r="2" />
    </IconBase>
  );
}

/** Sidebar — help (question mark). */
export function IconHelp(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.3a2.5 2.5 0 1 1 3.4 2.3c-.9.4-1.4 1-1.4 2.1" />
      <circle cx="11.5" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

/** Top bar — mobile drawer toggle. Replaces the "☰" glyph. */
export function IconMenu(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </IconBase>
  );
}

/** Generic close/dismiss (X) — for modals, drawers, dismissible banners. */
export function IconClose(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </IconBase>
  );
}

/* ── General-purpose glyphs (2026-09 redesign) ───────────────────────────
 * Direction-neutral where possible. Where direction matters (chevrons,
 * arrows) both are exported and the caller picks by `useDir()` — "forward"
 * points LEFT in Hebrew/Arabic. */

/** Sidebar — home (the dashboard is labelled "Home" since the redesign). */
export function IconHome(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M3.5 10.2 12 3.5l8.5 6.7V19a1.5 1.5 0 0 1-1.5 1.5h-4.2v-6h-5.6v6H5A1.5 1.5 0 0 1 3.5 19z" />
    </IconBase>
  );
}

/** Sidebar — risk surveyor (gauge). */
export function IconGauge(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M3.3 18.5a10 10 0 1 1 17.4 0" />
      <path d="m12 14 4.2-4.2" />
      <circle cx="12" cy="14" r="1.1" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function IconSearch(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.2-4.2" />
    </IconBase>
  );
}

export function IconChevronDown(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="m6 9 6 6 6-6" />
    </IconBase>
  );
}

export function IconChevronLeft(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="m15 18-6-6 6-6" />
    </IconBase>
  );
}

export function IconChevronRight(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="m9 18 6-6-6-6" />
    </IconBase>
  );
}

export function IconArrowLeft(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </IconBase>
  );
}

export function IconArrowRight(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </IconBase>
  );
}

export function IconCheck(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M20 6 9 17l-5-5" />
    </IconBase>
  );
}

export function IconCheckCircle(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
    </IconBase>
  );
}

export function IconPlus(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 5v14M5 12h14" />
    </IconBase>
  );
}

export function IconFilter(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 5h16l-6.2 7.3V18l-3.6 2v-7.7z" />
    </IconBase>
  );
}

export function IconDownload(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 20h14" />
    </IconBase>
  );
}

export function IconLogout(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M9.5 20.5H6A2 2 0 0 1 4 18.5v-13a2 2 0 0 1 2-2h3.5M15.5 16.5 20 12l-4.5-4.5M20 12H9.5" />
    </IconBase>
  );
}

export function IconGlobe(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </IconBase>
  );
}

export function IconClock(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 2" />
    </IconBase>
  );
}

export function IconAlertTriangle(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M10.3 4.2 2.6 17.6A2 2 0 0 0 4.3 20.6h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z" />
      <path d="M12 9.5v4" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function IconFlame(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 21a6.5 6.5 0 0 1-6.5-6.5c0-2.4 1.3-4.1 2.6-5.4.2 1.6 1 2.7 2.1 3.2C10 9 11 5.7 13.8 3c.3 2.8 1.8 4.6 3.1 6.1A7.4 7.4 0 0 1 18.5 14.5 6.5 6.5 0 0 1 12 21z" />
    </IconBase>
  );
}

export function IconHardHat(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M3 17.5h18M4.5 17.5v-2.8a7.5 7.5 0 0 1 15 0v2.8" />
      <path d="M10 7.5V5.2h4v2.3M9 12V8.3M15 12V8.3" />
    </IconBase>
  );
}

export function IconVideoOff(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M15 10.3 21 7.5v9l-4.3-2M11 7h2a2 2 0 0 1 2 2v2M15 15.5V15a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h.5" />
      <path d="M3 3l18 18" />
    </IconBase>
  );
}

export function IconFileText(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </IconBase>
  );
}

export function IconMore(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="5.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

export function IconPhone(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M21 16.4v2.7a1.8 1.8 0 0 1-2 1.8 17.8 17.8 0 0 1-7.8-2.8 17.5 17.5 0 0 1-5.4-5.4A17.8 17.8 0 0 1 3 4.9 1.8 1.8 0 0 1 4.8 3h2.7a1.8 1.8 0 0 1 1.8 1.5c.1.9.3 1.7.6 2.5a1.8 1.8 0 0 1-.4 1.9L8.3 10.1a14.4 14.4 0 0 0 5.4 5.4l1.2-1.2a1.8 1.8 0 0 1 1.9-.4c.8.3 1.7.5 2.5.6a1.8 1.8 0 0 1 1.5 1.9z" />
    </IconBase>
  );
}

export function IconMaximize(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M8 3.5H5.5a2 2 0 0 0-2 2V8M20.5 8V5.5a2 2 0 0 0-2-2H16M3.5 16v2.5a2 2 0 0 0 2 2H8M16 20.5h2.5a2 2 0 0 0 2-2V16" />
    </IconBase>
  );
}

export function IconEye(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </IconBase>
  );
}

/** Forward / hand off (paper plane). */
export function IconSend(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M21 3 10.5 13.5M21 3l-6.5 18-4-7.5-7.5-4z" />
    </IconBase>
  );
}

export function IconUserPlus(props: IconProps) {
  return (
    <IconBase {...props}>
      <circle cx="10" cy="8" r="3.4" />
      <path d="M3.5 20c0-4 2.9-6.6 6.5-6.6 1.5 0 2.9.4 4 1.2M18.5 13v6M15.5 16h6" />
    </IconBase>
  );
}

export function IconCalendar(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </IconBase>
  );
}

export function IconBolt(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M13 3 5 13.5h6L10 21l8-10.5h-6z" />
    </IconBase>
  );
}

export type Icon = ComponentType<IconProps>;

/** Keyed by `NavItem.key` in `components/layout/Shell.tsx`. */
export const NAV_ICON_MAP: Record<string, Icon> = {
  dashboard: IconHome,
  monitor: IconMonitor,
  alerts: IconAlerts,
  aiInspector: IconGauge,
  compliance: IconCompliance,
  pinkas: IconPinkas,
  reports: IconReports,
  cameras: IconCameras,
  zones: IconZones,
  workers: IconWorkers,
  vehicles: IconVehicles,
  aiHub: IconAiHub,
  settings: IconSettings,
  help: IconHelp,
};
